/**
 * Data Access Layer for Places
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import type { Place, PlaceCreate, PlaceUpdate, PlaceRoleDefault, PlaceRoleDayDefault } from './types.js';

export async function getPlace(id: string): Promise<Place | null> {
  const { data, error } = await getClient()
    .from(TABLES.places)
    .select('*, emergency_contact_phone, address')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Place;
}

export async function getPlaceBySlug(slug: string): Promise<Place | null> {
  const { data, error } = await getClient()
    .from(TABLES.places)
    .select('*')
    .eq('slug', slug)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Place;
}

export async function listPlaces(filters?: {
  type?: string;
  is_active?: boolean;
  parent_id?: string | null;
  query?: string;
}): Promise<Place[]> {
  let query = getClient().from(TABLES.places).select('*, emergency_contact_phone, address');

  if (filters?.type) {
    query = query.eq('type', filters.type);
  }
  if (filters?.is_active !== undefined) {
    query = query.eq('is_active', filters.is_active);
  }
  if (filters?.parent_id !== undefined) {
    if (filters.parent_id === null) {
      query = query.is('parent_id', null);
    } else {
      query = query.eq('parent_id', filters.parent_id);
    }
  }
  if (filters?.query && filters.query.trim()) {
    const searchTerm = filters.query.trim();
    query = query.or(`name.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%`);
  }

  const { data, error } = await query.order('name');

  if (error) throw error;
  console.log('listPlaces response sample:', data?.[0]);
  return (data || []) as Place[];
}

export async function createPlace(input: PlaceCreate): Promise<Place> {
  const { data, error } = await getClient()
    .from(TABLES.places)
    .insert(input)
    .select()
    .single();

  if (error) throw error;
  return data as Place;
}

export async function updatePlace(id: string, input: PlaceUpdate): Promise<Place> {
  const { data, error } = await getClient()
    .from(TABLES.places)
    .update(input)
    .eq('id', id)
    .select('*, emergency_contact_phone, address')
    .single();

  if (error) throw error;
  console.log('updatePlace response:', data);
  return data as Place;
}

export async function deletePlace(id: string): Promise<void> {
  const { error } = await getClient()
    .from(TABLES.places)
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function deletePlaces(ids: string[]): Promise<void> {
  if (ids.length === 0) return;

  const { error } = await getClient()
    .from(TABLES.places)
    .delete()
    .in('id', ids);

  if (error) throw error;
}

// ============================================
// Place role defaults (per-stage staffing prefill)
// ============================================

export async function listPlaceRoleDefaults(placeId: string): Promise<PlaceRoleDefault[]> {
  const { data, error } = await getClient()
    .from(TABLES.place_role_defaults)
    .select('*')
    .eq('place_id', placeId);

  if (error) throw error;
  return (data || []) as PlaceRoleDefault[];
}

/** Replace a stage's full set of default role requirements in one go. */
export async function setPlaceRoleDefaults(
  placeId: string,
  defaults: Array<{ role_id: string; count: number }>,
): Promise<void> {
  const client = getClient();

  const { error: deleteError } = await client
    .from(TABLES.place_role_defaults)
    .delete()
    .eq('place_id', placeId);
  if (deleteError) throw deleteError;

  if (defaults.length > 0) {
    const rows = defaults.map((d) => ({
      place_id: placeId,
      role_id: d.role_id,
      count: d.count,
    }));
    const { error: insertError } = await client.from(TABLES.place_role_defaults).insert(rows);
    if (insertError) throw insertError;
  }
}

// ============================================
// Place role day-overrides (per-date staffing)
// ============================================

export async function listPlaceRoleDayDefaults(placeId: string): Promise<PlaceRoleDayDefault[]> {
  const { data, error } = await getClient()
    .from(TABLES.place_role_day_defaults)
    .select('*')
    .eq('place_id', placeId)
    .order('day');

  if (error) throw error;
  return (data || []) as PlaceRoleDayDefault[];
}

/** Replace all day-override rows for a stage in one go. */
export async function setPlaceRoleDayDefaults(
  placeId: string,
  overrides: Array<{ role_id: string; day: string; count: number }>,
): Promise<void> {
  const client = getClient();

  const { error: deleteError } = await client
    .from(TABLES.place_role_day_defaults)
    .delete()
    .eq('place_id', placeId);
  if (deleteError) throw deleteError;

  if (overrides.length > 0) {
    const rows = overrides.map((o) => ({
      place_id: placeId,
      role_id: o.role_id,
      day: o.day,
      count: o.count,
    }));
    const { error: insertError } = await client.from(TABLES.place_role_day_defaults).insert(rows);
    if (insertError) throw insertError;
  }
}
